import { useState, useRef, useMemo, useEffect, type FormEvent } from 'react';
import { X, Plus, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { useCreateWorkflowTemplate, useCreateWorkflowType } from '@/hooks/useWorkflows';
import type { WorkflowType, WorkflowEnvironment } from '@/lib/types';

interface Props {
  types: WorkflowType[];
  onClose: () => void;
}

export default function CreateTemplateModal({ types, onClose }: Props) {
  const [typeName, setTypeName] = useState('');
  const [typeSearch, setTypeSearch] = useState('');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [newTypeName, setNewTypeName] = useState('');
  const [subtype, setSubtype] = useState('');
  const [version, setVersion] = useState('1.0');
  const [environment, setEnvironment] = useState<WorkflowEnvironment>('development');
  const [body, setBody] = useState('');
  const [description, setDescription] = useState('');
  const [showNewType, setShowNewType] = useState(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const createTemplate = useCreateWorkflowTemplate();
  const createType = useCreateWorkflowType();

  const filteredTypes = useMemo(() => {
    if (!typeSearch.trim()) return types;
    const q = typeSearch.toLowerCase();
    return types.filter(
      (t) => t.name.toLowerCase().includes(q) || (t.description?.toLowerCase().includes(q) ?? false)
    );
  }, [types, typeSearch]);

  const selectType = (type: WorkflowType) => {
    setTypeName(type.name);
    setTypeSearch(type.name);
    setShowTypeDropdown(false);
    setHighlightedIndex(-1);
  };

  const handleTypeInputKeyDown = (e: React.KeyboardEvent) => {
    if (!showTypeDropdown) {
      if (e.key === 'ArrowDown') {
        setShowTypeDropdown(true);
        setHighlightedIndex(0);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => (i < filteredTypes.length - 1 ? i + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => (i > 0 ? i - 1 : filteredTypes.length - 1));
    } else if (e.key === 'Enter' && highlightedIndex >= 0 && filteredTypes[highlightedIndex]) {
      e.preventDefault();
      selectType(filteredTypes[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowTypeDropdown(false);
      setTypeSearch(typeName);
    }
  };

  // Clean up blur timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  // Scroll highlighted item into view during keyboard navigation
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const item = dropdownRef.current.querySelector(`[data-index="${highlightedIndex}"]`);
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    try {
      // Create new type if needed
      let finalTypeName = typeName;
      if (showNewType && newTypeName) {
        await createType.mutateAsync({ name: newTypeName });
        finalTypeName = newTypeName;
      }

      if (!finalTypeName) {
        toast.error('Please select or create a workflow type');
        return;
      }

      await createTemplate.mutateAsync({
        type_name: finalTypeName,
        subtype,
        version,
        environment,
        body,
        description: description || undefined,
      });

      toast.success('Template created successfully');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create template');
    }
  };

  const isLoading = createTemplate.isPending || createType.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-xl font-semibold">Create Workflow Template</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md hover:bg-muted transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form id="create-template-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Type Selection */}
          <div className="space-y-2">
            <label htmlFor="template-type" className="text-sm font-medium">Workflow Type</label>
            {!showNewType ? (
              <div className="relative">
                <div className="flex gap-2">
                  <input
                    id="template-type"
                    type="text"
                    role="combobox"
                    aria-expanded={showTypeDropdown}
                    aria-controls="type-dropdown-listbox"
                    aria-autocomplete="list"
                    aria-activedescendant={highlightedIndex >= 0 ? `type-option-${highlightedIndex}` : undefined}
                    value={typeSearch}
                    onChange={(e) => {
                      setTypeSearch(e.target.value);
                      setShowTypeDropdown(true);
                      setHighlightedIndex(-1);
                      if (!e.target.value.trim()) setTypeName('');
                    }}
                    onFocus={() => {
                      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
                      setShowTypeDropdown(true);
                      setTypeSearch('');
                      setHighlightedIndex(-1);
                    }}
                    onBlur={() => {
                      blurTimeoutRef.current = setTimeout(() => {
                        setShowTypeDropdown(false);
                        setTypeSearch(typeName);
                      }, 150);
                    }}
                    onKeyDown={handleTypeInputKeyDown}
                    placeholder="Search workflow types..."
                    className="flex-1 px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                    autoComplete="off"
                    required={!showNewType && !typeName}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewType(true)}
                    className="px-3 py-2 rounded-md bg-secondary hover:bg-secondary/80 transition-colors"
                    aria-label="Create new workflow type"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                {showTypeDropdown && (
                  <div
                    ref={dropdownRef}
                    id="type-dropdown-listbox"
                    role="listbox"
                    className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-border bg-background shadow-lg"
                  >
                    {filteredTypes.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-muted-foreground">No types found</div>
                    ) : (
                      filteredTypes.map((type, index) => (
                        <button
                          key={type.name}
                          id={`type-option-${index}`}
                          type="button"
                          role="option"
                          aria-selected={typeName === type.name}
                          data-index={index}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectType(type)}
                          className={cn(
                            'w-full text-left px-3 py-2 text-sm transition-colors',
                            index === highlightedIndex ? 'bg-primary/10 text-primary' : 'hover:bg-muted',
                            typeName === type.name && 'font-medium'
                          )}
                        >
                          {type.name}
                          {type.description && (
                            <span className="text-muted-foreground"> — {type.description}</span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  id="template-type"
                  type="text"
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  placeholder="new-type-name"
                  className="flex-1 px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 font-mono"
                  required={showNewType}
                />
                <button
                  type="button"
                  onClick={() => {
                    setShowNewType(false);
                    setNewTypeName('');
                  }}
                  className="px-3 py-2 rounded-md bg-secondary hover:bg-secondary/80 transition-colors"
                  aria-label="Cancel new workflow type"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Types are normalized to lowercase with hyphens (e.g., photoshoot, aiedit-background-removal)
            </p>
          </div>

          {/* Subtype */}
          <div className="space-y-2">
            <label htmlFor="template-subtype" className="text-sm font-medium">Subtype</label>
            <input
              id="template-subtype"
              type="text"
              value={subtype}
              onChange={(e) => setSubtype(e.target.value)}
              placeholder="e.g., flux, sdxl, default"
              className="w-full px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
              required
            />
            <p className="text-xs text-muted-foreground">
              Variant or model type (case-insensitive)
            </p>
          </div>

          {/* Version & Environment */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="template-version" className="text-sm font-medium">Version</label>
              <input
                id="template-version"
                type="text"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="1.0"
                className="w-full px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="template-environment" className="text-sm font-medium">Environment</label>
              <select
                id="template-environment"
                value={environment}
                onChange={(e) => setEnvironment(e.target.value as WorkflowEnvironment)}
                className="w-full px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="development">Development</option>
                <option value="staging">Staging</option>
                <option value="production">Production</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label htmlFor="template-description" className="text-sm font-medium">Description (optional)</label>
            <input
              id="template-description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this template"
              className="w-full px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {/* Body */}
          <div className="space-y-2">
            <label htmlFor="template-body" className="text-sm font-medium">Template Body (JSON with placeholders)</label>
            <textarea
              id="template-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder='{"workflow": {...}, "seed": "<seed>", "prompt": "<positive_prompt>"}'
              className="w-full h-64 px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 font-mono text-sm"
              required
            />
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p>
                Use <code className="bg-muted px-1 rounded">&lt;variable_name&gt;</code> for placeholders.
                Variables will be auto-detected and can be defined after creation.
              </p>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md bg-secondary hover:bg-secondary/80 transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="create-template-form"
            disabled={isLoading}
            className="px-4 py-2 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Creating...' : 'Create Template'}
          </button>
        </div>
      </div>
    </div>
  );
}
